import { useEffect } from 'react';

// Default SEO values
const defaults = {
  siteName: 'TDF Psychometrics',
  siteUrl: 'https://traindriverpsychometrics.co.uk',
  defaultTitle: 'TDF Psychometrics | Train Driver Psychometric Test Preparation',
  defaultDescription: 'Official psychometric test preparation from the Train Driver Foundation. Practice all UK train driver assessment areas based on RIS-3751-TOM standards. Join 1000+ aspiring train drivers.',
  defaultImage: '/tdf-logo.png',
};

/**
 * SEO Component for dynamic page meta tags
 * Uses native DOM manipulation for better compatibility
 */
export const SEO = ({
  title,
  description,
  keywords,
  canonical,
  noIndex = false,
}) => {
  useEffect(() => {
    // Build page title - don't append site name if title already contains a pipe
    const pageTitle = title && title.trim() 
      ? (title.includes('|') ? title.trim() : `${title.trim()} | ${defaults.siteName}`)
      : defaults.defaultTitle;
    
    const pageDescription = description && description.trim() 
      ? description.trim() 
      : defaults.defaultDescription;

    const pageKeywords = keywords && keywords.trim()
      ? keywords.trim()
      : null;
    
    // Always use canonical URL (default to current path if not specified)
    const currentPath = canonical || window.location.pathname;
    const pageUrl = `${defaults.siteUrl}${currentPath === '/' ? '' : currentPath}`;
    
    const pageImage = `${defaults.siteUrl}${defaults.defaultImage}`;

    // Update document title
    document.title = pageTitle;

    // Helper to update/create meta tags
    const updateMeta = (selector, content, isProperty = false) => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else {
          meta.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update meta tags
    updateMeta('meta[name="title"]', pageTitle);
    updateMeta('meta[name="description"]', pageDescription);
    if (pageKeywords) {
      updateMeta('meta[name="keywords"]', pageKeywords);
    }
    updateMeta('meta[name="robots"]', noIndex ? 'noindex, nofollow' : 'index, follow');
    
    // Open Graph
    updateMeta('meta[property="og:title"]', pageTitle, true);
    updateMeta('meta[property="og:description"]', pageDescription, true);
    updateMeta('meta[property="og:url"]', pageUrl, true);
    updateMeta('meta[property="og:image"]', pageImage, true);
    updateMeta('meta[property="og:type"]', 'website', true);
    updateMeta('meta[property="og:site_name"]', defaults.siteName, true);
    
    // Twitter
    updateMeta('meta[name="twitter:title"]', pageTitle);
    updateMeta('meta[name="twitter:description"]', pageDescription);
    updateMeta('meta[name="twitter:url"]', pageUrl);
    updateMeta('meta[name="twitter:image"]', pageImage);
    updateMeta('meta[name="twitter:card"]', 'summary_large_image');

    // Update canonical - always set to ensure single indexed version
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    // Always set canonical to HTTPS non-www version
    canonicalLink.setAttribute('href', pageUrl.startsWith('https://') ? pageUrl : `https://traindriverpsychometrics.co.uk${currentPath}`);
  }, [title, description, keywords, canonical, noIndex]);

  return null; // This component doesn't render anything
};

// Pre-configured SEO for common pages
export const PageSEO = {
  home: {
    title: 'Train Driver Psychometric Tests UK | Practice & Pass',
    description: 'Practice all UK train driver psychometric tests. Built by industry experts and aligned to RIS-3751-TOM. Improve your chances and pass first time.',
    canonical: '/',
  },
  tests: {
    title: 'Practice Tests - All Psychometric Assessment Areas',
    description: 'Practice all train driver psychometric assessment areas: Attention, Vigilance, Memory, Reasoning, Perception, Reaction Time, Hand Coordination & more. RIS-3751-TOM aligned.',
    canonical: '/tests',
  },
  pricing: {
    title: 'Pricing - 30-Day & Annual Passes',
    description: 'Get unlimited access to all train driver psychometric assessment areas. 30-Day Pass £7.99 or Annual Pass £24.99 with FREE study guide. Start with 3 free test attempts.',
    canonical: '/pricing',
  },
  about: {
    title: 'About Us',
    description: 'TDF Psychometrics is the official psychometric test preparation platform from the Train Driver Foundation - the UK specialist in train driver recruitment and selection.',
    canonical: '/about',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with the Train Driver Foundation team. Questions about psychometric tests, assessments, or your train driver career? We are here to help.',
    canonical: '/contact',
  },
  skills: {
    title: 'Train Driver Skills - RIS-3751-TOM Requirements',
    description: 'Learn the 8 essential skills required to become a train driver: Communication, Observation (OUDA), Multiskilling, Concentration, Reactions, Memory, Prioritising & Decision Making.',
    canonical: '/skills',
  },
  faq: {
    title: 'FAQ - Train Driver Psychometric Assessment Questions',
    description: 'Official answers about UK train driver psychometric assessments based on RIS-3751-TOM Issue 4. Learn about attempt rules, eligibility, adjustments & more.',
    canonical: '/faq',
  },
  dashboard: {
    title: 'Your Dashboard',
    description: 'Track your train driver psychometric test progress, scores, and performance analytics. View your strengths and areas for development.',
    canonical: '/dashboard',
    noIndex: true, // Don't index user dashboards
  },
  login: {
    title: 'Sign In',
    description: 'Sign in to your TDF Psychometrics account to continue your train driver psychometric test preparation.',
    canonical: '/login',
    noIndex: true,
  },
  register: {
    title: 'Create Account - Get 3 Free Test Attempts',
    description: 'Create your free TDF Psychometrics account and get 3 free test attempts. Start preparing for your train driver psychometric assessment today.',
    canonical: '/register',
  },
  resources: {
    title: 'Resources & Guides | Train Driver Career Advice',
    description: 'Free resources, guides and articles about train driver psychometric tests, career advice, and industry research. Based on official RSSB studies.',
    canonical: '/resources',
  },
  forumSnippets: {
    title: 'Forum Snippets | Ready-to-Share Train Driver Answers',
    description: 'Expert answers to common train driver questions. Copy and share on forums, Reddit, and social media. Based on official RSSB research.',
    canonical: '/resources/forum-snippets',
  },
};

export default SEO;
